import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles  } from '@mui/styles';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { IconButton as ReactIconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { lighten  } from '@mui/material/styles';
import {
  Add,
  Edit,
  Delete,
  Cancel,
  ImportExport,
  CloudUpload
} from '@mui/icons-material';
import { toCurrency } from '../../utils/currency';
import { WithTracking } from "../WithTracking";
import Filter from './Filter';
import Settings from './Settings';
import Clone from './Clone';
import PushSettings from "../PushSettings";

const IconButton = WithTracking(ReactIconButton);

const styles = theme => ({
  root: {
    paddingRight: theme.spacing(1),
    minHeight: theme.spacing(6)
  },
  highlight:
    theme.palette.mode === 'light'
      ? {
        color: theme.palette.primary.main,
        backgroundColor: lighten(theme.palette.primary.light, 0.85)
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark
      },
  inline: {
    display: 'inline-flex',
    verticalAlign: 'Middle'
  },
  spacer: {
    flex: '1 1 100%'
  },
  actions: {
    color: theme.palette.primary,
    display: 'flex'
  },
  title: {
    flex: '0 0 auto'
  },
  filterBadge: {
    background: theme.palette.primary.dark,
    color: 'white',
    marginLeft: theme.spacing(2)
  },
  badge: {
    color: 'white',
    top: 13,
    right: -43,
    padding: 15,
    fontWeight: 900
  },
  filters: {
    display: 'flex',
  },
  deleteIcon: {
    color: '#cccccc',
    "&:hover": {
      color: 'white !important'
    }
  },
  selectedAmount: {
    flex: '0 0 auto',
    fontWeight: 'bold'
  }
});

class TableToolbar extends Component {

  constructor(props) {
    super(props);
    this.state = {
      numSelected: props.numSelected,
      pushSettings: false
    }
    this.cancelImplemented = true;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ numSelected: nextProps.numSelected });
  }

  removeFilter = evt => {
    let element = evt.target;
    if (element.nodeName !== 'DIV') {
      while ((element = element.parentElement) && element && element.nodeName !== 'DIV'); // eslint-disable-line no-cond-assign
    }
    const id = element.getAttribute('id');
    const { filterCriteria } = this.props;
    const field = filterCriteria.find(id);
    if (field) {
      field.clear();
    }
  };

  displayFilters = () => {
    const { classes, filterCriteria } = this.props;

    if (filterCriteria.isEmpty) return false;
    return filterCriteria.activeFields.map(f => {
      return (
        <Chip
          label={`${f.label}: ${f.displayValue}`}
          className={classes.filterBadge}
          id={f.id}
          key={f.id}
          onDelete={this.removeFilter}
          classes={{
            deleteIcon: classes.deleteIcon
          }}
        />
      )
    })
  };

  /**
   * Toggle settings, so if it comes in true we make it false and do nothing else
   */
  togglePushSettings = () => {
    this.setState(prevState => ({ pushSettings: !prevState.pushSettings }));
  };


  render() {
    const {
      title,
      plain,
      classes,
      settings,
      rowCount,
      lineItemCount,
      filterable,
      autosaveFilters,
      canAdd,
      onAdd,
      canEdit,
      onEdit,
      canClone,
      onClone,
      canDelete,
      canCancel,
      onDelete,
      onCancel,
      canUpload,
      disabledUpload,
      onUploadSelected,
      canExport,
      onExport,
      canSelectExport,
      onSelectExport,
      onColumnToggle,
      onSettingSwitch,
      onRestoreSettings,
      onDensityChange,
      suppressCount,
      filterCriteria,
      summaryDisplay,
      onToggleExpand,
      showSelectedAmount,
      selectedAmount,
      isReadOnly,
      setPushData,
      pushPlacementsFromOrder,
      pushPlacementsFromPackage,
      onPushPackages,
      pushType,
      whitelist,
    } = this.props;

    const { numSelected } = this.state;
    const rowCountDisplay = (rowCount === lineItemCount) ? rowCount : `${rowCount}:${lineItemCount}`;
    let countTooltip = title;
    if (title  === 'Placements') {
      countTooltip = 'Placements : Line Items in Ad Manager';
    }

    const summaryStyle = {
      cursor: summaryDisplay ? "pointer" : "default",
      borderTop: "1px solid #ccc",
      padding: "6px 0"
    };

    return (
      <div style={summaryStyle}
        onClick={e => {if (summaryDisplay) { onToggleExpand(false) }}}>
        <Toolbar
          className={classNames(classes.root, {
            [classes.highlight]: numSelected > 0
          })}
        >
          <div className={classes.title}>
            {numSelected > 0 && (
              <Badge
                color="primary"
                badgeContent={numSelected}
                classes={{ badge: classes.badge }}
              >
                <Typography color="inherit" variant="subtitle1">
                  Selected {title}
                </Typography>
              </Badge>
            )}
            {numSelected === 0 && !suppressCount && (
            <div className={classes.inline}>
              <Tooltip title={countTooltip} placement="top">
                <Badge
                  color="primary"
                  badgeContent={rowCountDisplay}
                  max={6000}
                  classes={{ badge: classes.badge }}
                >
                  <Typography color="inherit" variant="subtitle1">
                    {title}
                  </Typography>
                </Badge>
              </Tooltip>
            </div>
            )}
            {numSelected === 0 && suppressCount && (
              <div className={classes.inline}>
                <Typography color="inherit" variant="subtitle1">
                  {title}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.spacer} />
          {showSelectedAmount && numSelected > 0 && (
            <div className={classes.selectedAmount}>
              Total Contracted Amount: {toCurrency(selectedAmount)}
            </div>
          )}
          {!summaryDisplay && (
          <div>
            {numSelected > 0 ? (
              <div className={classes.actions}>
                {canEdit && !isReadOnly && (
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      onClick={onEdit}
                      aria-label="Edit"
                      tracking={{action:'Edit'}}
                      size="large">
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {!disabledUpload && !isReadOnly && numSelected > 0 && canUpload && (
                  <Tooltip title="Push" placement="top">
                    <IconButton
                      onClick={this.togglePushSettings}
                      arial-label="Push"
                      tracking={{action:'Push Settings'}}
                      size="large">
                      <CloudUpload />
                    </IconButton>
                  </Tooltip>
                )}

                {canUpload && !disabledUpload && !isReadOnly && numSelected > 0 && (
                  <PushSettings
                    open={this.state.pushSettings}
                    order={null}
                    pushType={pushType}
                    onPushOrder={null}
                    onPushPackages={onPushPackages}
                    onUploadSelected={onUploadSelected}
                    pushPlacementsFromOrder={pushPlacementsFromOrder}
                    pushPlacementsFromPackage={pushPlacementsFromPackage}
                    onClose={this.togglePushSettings}
                    tracking={{action:'Push'}}
                    setPushData={setPushData}
                    whitelist={whitelist}
                    caller={'tabletoolbar'}
                  />
                )}

                {canUpload && disabledUpload && (
                  <Tooltip title="Order is Locked" placement="top"  tracking={{action:'Order is Locked'}}>
                    <IconButton arial-label="Push" disabled size="large">
                      <CloudUpload />
                    </IconButton>
                  </Tooltip>
                )}

                {canSelectExport && (
                  <Tooltip title="Export" placement="top">
                    <IconButton
                      onClick={onSelectExport}
                      arial-label="Export"
                      tracking={{action:'Export'}}
                      size="large">
                      <ImportExport />
                    </IconButton>
                  </Tooltip>
                )}

                {canClone && <Clone placement="top" onClone={onClone} />}

                {canDelete && !disabledUpload && !isReadOnly && (
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      aria-label="Delete"
                      onClick={onDelete}
                      color={"secondary"}
                      tracking={{action:'Delete'}}
                      size="large">
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                {canDelete && disabledUpload && (
                  <Tooltip title="Delete" placement="top">
                    <IconButton aria-label="Delete" color={"secondary"} disabled size="large">
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                {this.cancelImplemented && canCancel && !isReadOnly && (
                  <Tooltip title="Cancel Placement" placement="top">
                    <IconButton
                      aria-label="Cancel Placement"
                      onClick={onCancel}
                      color={"secondary"}
                      tracking={{action:'Cancel'}}
                      size="large">
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                )}

              </div>
            ) : (
              <div className={classes.actions}>
                {canAdd && !isReadOnly && (
                  <Tooltip title="Add New." placement="top" tracking={{action:'Add New'}}>
                    <IconButton onClick={onAdd} color="primary" aria-label="Add New" size="large">
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
                {filterable && (
                  <Filter
                    filterCriteria={this.props.filterCriteria}
                    title={title}
                    autosaveFilters={autosaveFilters}
                  />
                )}
                {canExport && (
                  <Tooltip title="Export" placement="top">
                    <IconButton
                      onClick={onExport}
                      arial-label="Export"
                      tracking={{action:'Export'}}
                      size="large">
                      <ImportExport />
                    </IconButton>
                  </Tooltip>
                )}

                {!plain && (
                  <Settings
                    title={title}
                    settings={settings}
                    density={settings.density}
                    columns={this.props.columns}
                    onColumnToggle={onColumnToggle}
                    onSwitch={onSettingSwitch}
                    onRestore={onRestoreSettings}
                    onDensityChange={onDensityChange}
                  />
                )}

              </div>
            )}
          </div>
          )}

        </Toolbar>
        {(filterCriteria.activeFields.length > 0 && (
          <Toolbar>
            <Typography variant="subtitle1" color="inherit">
              Filters:
            </Typography>
            <div className={classes.filters}>
              {this.displayFilters()}
            </div>
          </Toolbar>
        ))}
      </div>
    );
  }
}

TableToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  numSelected: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  disabledUpload: PropTypes.bool,
  showSelectedAmount: PropTypes.bool,
  selectedAmount: PropTypes.number
};

TableToolbar.defaultProps = {
  disabledUpload: false
};

export default withStyles(styles)(TableToolbar);
